import React, { FC, useCallback } from 'react';
import { FieldErrors } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import z from 'zod';

import { Box, Button, Flex, Heading, Link, Paragraph, Text } from '@rover/kibble/core';
import CheckboxField from '@rover/kibble/forms/fields/CheckboxField/CheckboxField';
import { CheckboxFieldOptionConfig } from '@rover/kibble/forms/fields/CheckboxField/CheckboxField.common';
import TextField from '@rover/kibble/forms/fields/TextField';
import { DataTypeOfZodSchema, Form, useKibbleForm } from '@rover/kibble/forms/index';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useFooterShim from '@rover/shared/js/hooks/useFooterShim';
import { useNewsletterSignupShim } from '@rover/shared/js/hooks/useNewsletterSignupShim';

import {
  EmailCaptureError,
  EmailCaptureInteract,
  EmailCaptureSubmit,
  EmailCaptureSubmitNoConsent,
} from './analyticsEvents';
import CheckIcon from './check.svg';
import EmailHelperMessage from './EmailHelperMessage';

const MarketingEmailCapture: FC = () => {
  const { footer } = useFooterShim();
  const { emailCapture } = footer || {};

  const { pageName } = usePageContext();

  const {
    signupData: { isSuccessful, isLoading, errorMessage },
    submitEmail: onSubmitEmail,
  } = useNewsletterSignupShim();

  const handleFormSubmit = useCallback(
    (data: FormData) => {
      emitAnalyticsEvent(new EmailCaptureSubmit({ pageName }));
      onSubmitEmail(data);
    },
    [onSubmitEmail, pageName]
  );

  const handleInvalidSubmit = (errors: FieldErrors<FormData>): void => {
    if (errors.email) {
      emitAnalyticsEvent(new EmailCaptureError({ pageName }));
    }
    if (errors.optIn) {
      emitAnalyticsEvent(new EmailCaptureSubmitNoConsent({ pageName }));
    }
  };

  const handleOnFocus = (): void => {
    emitAnalyticsEvent(new EmailCaptureInteract({ pageName }));
  };

  const { current: currentCountryConfiguration } = useCountryConfigurationShim();
  const needsOptIn = currentCountryConfiguration?.showEmailOptIn;

  const optIn = needsOptIn
    ? z.array(z.string()).min(1, 'Consent is required')
    : z.array(z.string()).optional();

  const schema = z.object({
    optIn,
    email: z.string().email({ message: 'Please enter a valid email' }),
  });

  type FormData = DataTypeOfZodSchema<typeof schema>;

  const { configureField, formProps, submitButtonProps } = useKibbleForm({
    schema,
    onValidSubmit: handleFormSubmit,
    onInvalidSubmit: handleInvalidSubmit,
  });

  const message = t`I consent to receive marketing communications from Rover.com and its affiliates and confirm that I am 18 years of age or older. I can unsubscribe at any time.`;

  const options: CheckboxFieldOptionConfig[] = [
    { primaryLabel: message, secondaryLabel: '', value: message },
  ];

  if (!emailCapture || (emailCapture && !emailCapture.visible)) return null;
  return (
    <Flex flexDirection="column">
      <Heading size="300" my="6x" as="div">
        {emailCapture.name}
      </Heading>
      <Form {...formProps} errorMessage={errorMessage || undefined} hideRequiredFieldWarning>
        <Paragraph size="100" textColor="secondary" fontWeight="regular">
          {t`Receive the latest adorable pet photos, care tips, training advice, product recommendations, and more.`}
        </Paragraph>
        <Flex flexDirection={['column', 'row', 'row']}>
          <Flex flexGrow="1">
            <TextField
              {...configureField('email')}
              disabled={isSuccessful}
              primaryLabel={t`Your Email...`}
              primaryLabelA11yHidden
              placeholder={t`Your Email...`}
              required={false}
              onFocus={handleOnFocus}
            />
          </Flex>
          {isSuccessful ? (
            <Flex mb="6x" ml={['0x', '4x', '4x']} alignItems="center" justifyContent="center">
              <Text
                fontWeight="semibold"
                textColor="success"
                mr="2x"
                sx={{ width: ['auto', 'min-content', 'min-content'] }}
              >
                {t`Subscribed!`}
              </Text>
              <CheckIcon />
            </Flex>
          ) : (
            <Flex
              width={['100%', 'auto', 'auto']}
              ml={['0x', '4x', '4x']}
              mb="6x"
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Button
                variant="default"
                loading={isLoading}
                alignSelf="flex-start"
                {...submitButtonProps}
                width={['100%', 'auto', 'auto']}
              >
                {t`Sign up`}
              </Button>
            </Flex>
          )}
        </Flex>
        {needsOptIn ? (
          <Box alignSelf="start" minWidth="fit-content">
            <CheckboxField
              {...configureField('optIn')}
              options={options}
              primaryLabel=""
              secondaryLabel=""
              required={false}
              disabled={isSuccessful}
              helperMessage={EmailHelperMessage}
              variant="legalConsent"
            />
          </Box>
        ) : (
          <>
            <Paragraph size="100" textColor="tertiary">
              <Trans>
                By providing my email address, I consent to receive marketing communications from
                Rover.com{'  '}and its affiliates and confirm that I am 18 years of age or older. I
                can unsubscribe at any time.
              </Trans>{' '}
            </Paragraph>
            <Paragraph size="100" textColor="tertiary">
              {emailCapture.items.length > 0 && (
                <Link href={emailCapture.items[0].url} variant="secondary" size="100" asInlineLink>
                  {emailCapture.items[0].name}
                </Link>
              )}
            </Paragraph>
          </>
        )}
      </Form>
    </Flex>
  );
};

export default MarketingEmailCapture;
